import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { PreviewRunState } from "@/lib/previewInsights";
import {
  MAX_TURN_PAGES,
  TURN_EVENT_KIND,
  TURN_PAGE_LIMIT,
  type JsonRecord,
  type PreviewReplayCreated,
  type PreviewRunEventPage,
  type ReplayPreviewRunRequest,
} from "@/lib/previewReplay";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { isServerAnsweredFailure } from "./queryClient";

/**
 * Replay from a turn — the two requests the feature makes (#1105).
 *
 * ONE READ: `GET /v1/projects/{projectId}/preview-runs/{previewRunRef}/events`
 * filtered to the `turn` family, paged on the server's cursor. Compact events
 * only — each carries its bodies BY REFERENCE (a hash, a size, a visibility
 * class, an omission reason), never text — which is all a boundary list needs
 * and all a polled read may fetch (approved design §10.2). The query string is
 * exactly `kind=turn&limit=100` and, on a later page, the cursor;
 * `src/invariants/previewEgress.test.tsx` asserts it on the bytes.
 *
 * ONE WRITE: `POST .../replays`, the audited command #1104 added. The body is
 * `replayRequestBody`'s five fields and nothing else; the source run is the
 * locator in the path. `retry: false` is load-bearing here exactly as it is
 * for session creation: a replay spends model calls, and an automatic second
 * attempt would be a second command — the idempotency key makes a DELIBERATE
 * retry safe (the service answers a reserved key from its reservation and
 * re-sends nothing), and that retry is a control the clinician presses.
 */

export type TurnEventsReading = {
  /** The run header, present on every answer whatever the projection holds. */
  run: PreviewRunState;
  /** Null when `run.contentAvailable` is false — not the same as no turns. */
  events: JsonRecord[] | null;
  /** True when `MAX_TURN_PAGES` were read and the server still had more. */
  truncated: boolean;
};

export function usePreviewRunTurnsQuery(
  projectId: string,
  previewRunRef: string,
  {
    enabled = true,
    pollIntervalMs = false,
  }: {
    /** False without `preview.inspect`, or while the run has no readable content. */
    enabled?: boolean;
    /**
     * A cadence, false to read once and on the refresh control only, or a
     * function of the LAST reading — how the replay dialog stops re-reading
     * a destination whose planned turns are all recorded. Decided from the
     * last answer and nothing else; TanStack keeps its default
     * `refetchIntervalInBackground: false`, so a hidden tab polls nothing.
     */
    pollIntervalMs?: number | false | ((reading: TurnEventsReading) => number | false);
  } = {},
): UseQueryResult<TurnEventsReading> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRunTurns(projectId, previewRunRef),
    async queryFn(): Promise<TurnEventsReading> {
      const events: JsonRecord[] = [];
      let afterSequence: number | undefined;
      let latestRun: PreviewRunState | undefined;
      for (let page = 0; page < MAX_TURN_PAGES; page += 1) {
        const query: { kind: typeof TURN_EVENT_KIND; limit: number; afterSequence?: number } =
          afterSequence === undefined
            ? { kind: TURN_EVENT_KIND, limit: TURN_PAGE_LIMIT }
            : { kind: TURN_EVENT_KIND, limit: TURN_PAGE_LIMIT, afterSequence };
        const answer: PreviewRunEventPage = unwrap(
          await client.GET(
            "/v1/projects/{projectId}/preview-runs/{previewRunRef}/events",
            { params: { path: { projectId, previewRunRef }, query } },
          ),
        );
        latestRun = answer.run;
        if (!answer.page) {
          return { run: answer.run, events: null, truncated: false };
        }
        events.push(...answer.page.events);
        // Page on `hasMore` and the cursor, never on a short page: the server
        // also spends a byte budget, so a page may be shorter than `limit`
        // with more behind it.
        const next = answer.page.nextAfterSequence;
        if (!answer.page.hasMore || next === null || next === undefined) {
          return { run: answer.run, events, truncated: false };
        }
        afterSequence = next;
      }
      // Assigned on the first iteration; `MAX_TURN_PAGES` is positive.
      return { run: latestRun as PreviewRunState, events, truncated: true };
    },
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    // A reading of a run at an instant, never a durable copy — the same
    // discipline as the run envelope (`usePreviewRunQuery`).
    staleTime: 0,
    gcTime: 0,
    // One attempt per read and one per press of the refresh control; a polled
    // read must not spend three requests on one failure (#1098 review).
    retry: false,
    refetchInterval: (query) => {
      if (query.state.status !== "success" || query.state.error) {
        return false;
      }
      if (typeof pollIntervalMs !== "function") {
        return pollIntervalMs;
      }
      const data = query.state.data;
      return data === undefined ? false : pollIntervalMs(data);
    },
  });
}

export function useReplayPreviewRunMutation(
  projectId: string,
  previewRunRef: string,
): UseMutationResult<PreviewReplayCreated, unknown, ReplayPreviewRunRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(body: ReplayPreviewRunRequest) {
      return unwrap(
        await client.POST(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/replays",
          { params: { path: { projectId, previewRunRef } }, body },
        ),
      );
    },
    // The run may become the qualifying preview the Lab's Accept button reads.
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
    },
    retry: false,
  });
}

/**
 * Why the command was not answered with a 201, classified by what came back
 * — the contract keeps three 409s and three 422s apart by code and field, and
 * two of them have recoveries that spend nothing.
 */
export type ReplayFailure =
  /** 409 `stale_preview_target_revision` — someone retargeted the Project; nothing was replayed. */
  | { kind: "stale_target"; currentRevision: number | undefined }
  /**
   * 409 `idempotency_conflict` — this attempt's key already names a DIFFERENT
   * command, here or on the target. Nothing was replayed and nothing about
   * the other command is disclosed; the recovery is a fresh key.
   */
  | { kind: "duplicate_command" }
  /** 409 `preview_admission_refused` — contract §8 refused the candidate's posture before any session existed. */
  | {
      kind: "admission_refused";
      admissionReason: string | undefined;
      refusalReason: string | undefined;
    }
  /** 422 `preview_target_not_enabled` — the target has no credential. */
  | { kind: "preview_target" }
  /** 422 on `candidateId` — not this Project's or expired. */
  | { kind: "candidate_unusable" }
  /** 422 `candidate_validator_mismatch` — rebuild on the current Editor deployment. */
  | { kind: "candidate_validator_mismatch" }
  /** 422 on `patientRef` — the fixture is no longer offered. */
  | { kind: "patient_gone" }
  /** 422 on `stopAfterTurn` — outside the published turn budget. */
  | { kind: "invalid_turn" }
  /**
   * 404 — the ONE generic answer for a source this account cannot replay from
   * at this address: unknown, another Project's, another environment's, or
   * bound to a target the Project no longer points at (ADR 0013 N2).
   */
  | { kind: "source_unavailable" }
  /** 403 — no `preview.create` for this Project, as the server sees it. */
  | { kind: "denied" }
  /** 401 — the sign-in is gone. */
  | { kind: "unauthenticated" }
  /** 501 — the command is not built in this environment. */
  | { kind: "unavailable" }
  /**
   * 502/503/504, or no answer at all: WHETHER A REPLAY STARTED IS UNKNOWN.
   * Transport failures happen after reservation; retrying with the SAME key
   * is the only safe recovery because it cannot spend a second budget.
   */
  | { kind: "unknown_dispatch"; answered: boolean }
  /** 503 `validator_pin_unavailable` — known pre-egress operator configuration failure. */
  | { kind: "validator_pin_unavailable" }
  /** Any other answered failure, carrying the machine code and request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifyReplayFailure(error: unknown): ReplayFailure {
  if (!isServerAnsweredFailure(error)) {
    return { kind: "unknown_dispatch", answered: false };
  }
  if (error instanceof ApiError) {
    if (error.code === "preview_admission_refused") {
      const admissionReason = error.envelope["previewAdmissionReason"];
      const refusalReason = error.envelope["finalQuestioningRefusalReason"];
      return {
        kind: "admission_refused",
        admissionReason:
          typeof admissionReason === "string" ? admissionReason : undefined,
        refusalReason:
          typeof refusalReason === "string" ? refusalReason : undefined,
      };
    }
    if (error.code === "candidate_validator_mismatch") {
      return { kind: "candidate_validator_mismatch" };
    }
    if (error.code === "validator_pin_unavailable") {
      return { kind: "validator_pin_unavailable" };
    }
    if (error.code === "idempotency_conflict") {
      return { kind: "duplicate_command" };
    }
    if (error.isConflict) {
      const current = error.envelope["currentPreviewTargetRevision"];
      return {
        kind: "stale_target",
        currentRevision: typeof current === "number" ? current : undefined,
      };
    }
    if (error.code === "preview_target_not_enabled") {
      return { kind: "preview_target" };
    }
    if (error.status === 404) {
      return { kind: "source_unavailable" };
    }
    if (error.isForbidden) {
      return { kind: "denied" };
    }
    if (error.isUnauthenticated) {
      return { kind: "unauthenticated" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.status === 422) {
      const field = error.envelope["field"];
      if (field === "patientRef") {
        return { kind: "patient_gone" };
      }
      if (field === "stopAfterTurn") {
        return { kind: "invalid_turn" };
      }
      return { kind: "candidate_unusable" };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "unknown_dispatch", answered: true };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // `isServerAnsweredFailure` is `instanceof ApiError`; spelled out so the
  // classifier is total by construction.
  return { kind: "unknown_dispatch", answered: false };
}
